import { memo, useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { Button } from "app/components/ui/Button/Button";
import QuotationPriceSummary from "app/pages/.shared/PriceSummary/QuotationPriceSummary";
import useOnScreen from "app/pages/.shared/useOnScreen";
import { useAnalytics } from "app/utils/analytics/useAnalytics";
import PaymentDescriptionTermsContainer from "app/pages/Booking/Payment/PaymentDescriptionTerms/PaymentDescriptionTermsContainer";
import PayNowNote from "app/pages/.shared/PayNow/PayNowNote";
import { useShowDepositFlag } from "app/pages/.shared/Deposit/useShowDepositFlag";
import MilesEarnedContainer from "app/pages/.shared/MilesEarned/MilesEarnedContainer";
import "./QuotationSubmit.scss";
import { ArrowRightIcon } from "app/icons/navigation";

const QuotationSubmit = ({
	isLoading = false,
	submitting = false,
	isHotelOnly = false,
	enableFlexRateChoice = false,
	onShowFlexRateChoice = () => {},
	availablePaymentMethods = [],
	depositPercentage,
	hasChildren,
	total,
	pricePerPerson,
	savingsTotalAmount,
	isBookingEligibleForDeposit,
}) => {
	const { track } = useAnalytics();
	const ref = useRef();
	const isElementOnScreen = useOnScreen({ ref, triggerOnce: true });

	const showDepositFlagValue = useShowDepositFlag();

	const payNowNote = total > 0 && (
		<PayNowNote
			total={total}
			depositPercentage={depositPercentage}
			availablePaymentMethods={availablePaymentMethods}
			displayAmountSuffix={false}
		/>
	);

	let paymentDescriptionTermsContent;

	if (isBookingEligibleForDeposit && showDepositFlagValue) {
		paymentDescriptionTermsContent = payNowNote;
	} else {
		paymentDescriptionTermsContent = <PaymentDescriptionTermsContainer />;
	}

	useEffect(() => {
		if (isElementOnScreen && !isLoading) {
			track("quote_view_CTA");
		}
	}, [isElementOnScreen, isLoading]);

	const handleOnClick = useCallback(() => {
		if (enableFlexRateChoice) {
			onShowFlexRateChoice();
		}
		track("goToPaymentPageClick");
	}, [enableFlexRateChoice]);

	return (
		<div className="quotation-submit" ref={ref}>
			{isLoading ? (
				<div className="quotation-submit__button">
					<Button
						variant="primary"
						size="lg"
						fullWidth
						type="submit"
						disabled={true}
						loading={true}
						data-testid="quote-submit-button"
					>
						<FormattedMessage id="general.button.loading.label" />
					</Button>
				</div>
			) : (
				<>
					<QuotationPriceSummary
						total={total}
						savingsTotalAmount={savingsTotalAmount}
						hasChildren={hasChildren}
						isHotelOnly={isHotelOnly}
					/>

					<div className="quotation-submit__button">
						<Button
							variant="primary"
							size="lg"
							fullWidth
							endIcon={<ArrowRightIcon />}
							type={enableFlexRateChoice ? "button" : "submit"}
							id="quotation-cta"
							disabled={submitting || isLoading}
							loading={submitting || isLoading}
							data-testid="quote-submit-button"
							onClick={handleOnClick}
						>
							<FormattedMessage id="quotation.button.cta" />
						</Button>
					</div>

					<MilesEarnedContainer
						price={pricePerPerson}
						prefixMessageId="miles.earned.search.engine.prefix"
						suffixLabel={<FormattedMessage id="miles.earned.suffix" />}
					/>

					{paymentDescriptionTermsContent}
				</>
			)}
		</div>
	);
};

QuotationSubmit.propTypes = {
	isHotelOnly: PropTypes.bool,
	submitting: PropTypes.bool,
	isLoading: PropTypes.bool,
	enableFlexRateChoice: PropTypes.bool,
	onShowFlexRateChoice: PropTypes.func,
	depositPercentage: PropTypes.number,
	availablePaymentMethods: PropTypes.array,
	hasChildren: PropTypes.bool,
	total: PropTypes.number,
	pricePerPerson: PropTypes.number,
	savingsTotalAmount: PropTypes.number,
	isBookingEligibleForDeposit: PropTypes.bool,
};

export default memo(QuotationSubmit);
